import { useAtom } from 'jotai'
import { type DexCoords } from '#app/data/models/coords.model.js'
import { type DexEntityType } from '#app/data/models/entity.model.js'
import {
  DexAnchorPosition,
  type DexOverlayInstance,
  type DexOverlayType,
} from '#app/data/models/overlay.model.js'
import { openedOverlaysAtom } from '#app/data/states/overlay-state.ts'

export type ViewportZone = {
  position: DexAnchorPosition
  vertical: DexAnchorPosition | undefined
  horizontal: DexAnchorPosition | undefined
  centerProximity: number
}

export type OverlayFilter = Partial<
  Pick<DexOverlayInstance, 'open'> & {
    type: DexOverlayType[] | DexOverlayType
    targetId: string[] | string
    targetType: DexEntityType[] | DexEntityType
  }
>

export default function useOverlays() {
  const [openedOverlays, setOpenedOverlays] = useAtom(openedOverlaysAtom)

  /**
   * Filter overlays
   *
   * @param overlays
   * @param filter
   * @param reverseFilter
   * @returns
   */
  function filterOverlays(
    overlays: DexOverlayInstance[],
    filter: OverlayFilter,
    reverseFilter?: boolean,
  ) {
    const types = filter.type
      ? !Array.isArray(filter.type)
        ? [filter.type]
        : filter.type
      : []
    const targetIds = filter.targetId
      ? !Array.isArray(filter.targetId)
        ? [filter.targetId]
        : filter.targetId
      : []
    const targetTypes = filter.targetType
      ? !Array.isArray(filter.targetType)
        ? [filter.targetType]
        : filter.targetType
      : []

    return overlays.filter(overlay => {
      const matchesType = !types.length || types.includes(overlay.type)
      const matchesOpen =
        filter.open === undefined || overlay.open === filter.open
      const matchesTargetType =
        !targetTypes.length ||
        (overlay.targetType && targetTypes.includes(overlay.targetType))
      const matchesTargetId =
        !targetIds.length ||
        (overlay.targetIds &&
          targetIds.some(x => overlay.targetIds?.includes(x)))

      // Check if the overlay should be included based on the filter
      const isIncluded = [
        matchesType,
        matchesOpen,
        matchesTargetType,
        matchesTargetId,
      ].every(Boolean)

      // Reverse the logic if reverseFilter is enabled
      return reverseFilter ? !isIncluded : isIncluded
    })
  }

  /**
   * Get overlay by filter
   *
   * @param filter
   * @returns
   */
  function getOverlay(
    filter: Partial<
      Pick<
        DexOverlayInstance,
        'type' | 'open' | 'targetIds' | 'targetType' | 'backdropEnabled'
      >
    >,
  ) {
    return filterOverlays(openedOverlays, filter)[0]
  }

  /**
   * Get overlay by id
   *
   * @param id
   * @returns
   */
  function getOverlayById(id: string) {
    return openedOverlays.find(overlay => overlay.id === id)
  }

  /**
   * Get overlays by filter
   *
   * @param filter
   * @returns
   */
  function getOverlays(
    filter: Partial<
      Pick<
        DexOverlayInstance,
        'type' | 'open' | 'targetIds' | 'targetType' | 'backdropEnabled'
      >
    >,
  ) {
    return filterOverlays(openedOverlays, filter)
  }

  /**
   * Get overlays by target id
   *
   * @param id
   * @returns
   */
  function getOverlaysByTargetId(id: string) {
    return openedOverlays.filter(overlay => overlay.targetIds?.includes(id))
  }

  /**
   * Get the viewport zone of a coordinate
   *
   * @param {
   *  coords: DexCoords
   *  centerThreshold?: number
   *  centerThresholdShape: 'circle' | 'rectangle'
   *  centerThresholdAspectRatio: '1:1' | 'x:y'
   *  enableAxisMiddleZone?: boolean
   * }
   * @returns
   */
  function getViewportZone({
    coords,
    centerThreshold = 0.1,
    centerThresholdShape = 'circle',
    centerThresholdAspectRatio = '1:1',
    enableAxisMiddleZone,
  }: {
    coords: DexCoords
    centerThreshold?: number
    centerThresholdShape?: 'circle' | 'rectangle'
    centerThresholdAspectRatio?: '1:1' | 'x:y'
    enableAxisMiddleZone?: boolean
  }): ViewportZone | undefined {
    if (!coords) return

    const centerX = window.innerWidth / 2
    const centerY = window.innerHeight / 2

    let isYTop = false
    let isYBottom = false
    let isXLeft = false
    let isXRight = false
    let centerProximity = 0

    if (centerThresholdShape === 'circle') {
      const minCenterThresholdRadius = 40
      let centerRadiusX, centerRadiusY

      if (centerThresholdAspectRatio === '1:1') {
        const centerRadius = Math.max(
          minCenterThresholdRadius,
          (centerThreshold * Math.min(window.innerWidth, window.innerHeight)) /
            2,
        )
        centerRadiusX = centerRadius
        centerRadiusY = centerRadius
      } else {
        centerRadiusX = (centerThreshold * window.innerWidth) / 2
        centerRadiusY = (centerThreshold * window.innerHeight) / 2
      }

      const distanceX = (coords.x - centerX) / centerRadiusX
      const distanceY = (coords.y - centerY) / centerRadiusY
      const distance = Math.sqrt(
        Math.pow(distanceX, 2) + Math.pow(distanceY, 2),
      )

      centerProximity = distance > 1 ? 1 / distance : 1

      isYTop = coords.y < centerY - centerRadiusY
      isYBottom = coords.y > centerY + centerRadiusY
      isXLeft = coords.x < centerX - centerRadiusX
      isXRight = coords.x > centerX + centerRadiusX
    }

    if (centerThresholdShape === 'rectangle') {
      const minCenterThresholdSize = 40
      let centerThresholdX, centerThresholdY

      if (centerThresholdAspectRatio === '1:1') {
        const size = Math.max(
          minCenterThresholdSize,
          (centerThreshold * Math.min(window.innerWidth, window.innerHeight)) /
            2,
        )
        centerThresholdX = size
        centerThresholdY = size
      } else {
        centerThresholdX = Math.max(
          minCenterThresholdSize,
          (centerThreshold * window.innerWidth) / 2,
        )
        centerThresholdY = Math.max(
          minCenterThresholdSize,
          (centerThreshold * window.innerHeight) / 2,
        )
      }

      const maxDistanceX = window.innerWidth / 2 - centerThresholdX
      const maxDistanceY = window.innerHeight / 2 - centerThresholdY
      const distanceX = Math.abs(centerX - coords.x) - centerThresholdX
      const distanceY = Math.abs(centerY - coords.y) - centerThresholdY

      if (distanceX <= 0 && distanceY <= 0) {
        centerProximity = 1
      } else {
        const dx = Math.max(0, distanceX)
        const dy = Math.max(0, distanceY)
        const distance = Math.sqrt(dx * dx + dy * dy)
        const maxDistance = Math.sqrt(
          maxDistanceX * maxDistanceX + maxDistanceY * maxDistanceY,
        )
        centerProximity = 1 - Math.min(1, distance / maxDistance)
      }

      isYTop = coords.y < centerY - centerThresholdY
      isYBottom = coords.y > centerY + centerThresholdY
      isXLeft = coords.x < centerX - centerThresholdX
      isXRight = coords.x > centerX + centerThresholdX
    }

    let vertical
    let horizontal

    if (enableAxisMiddleZone) {
      if (isYTop) {
        vertical = DexAnchorPosition.TOP
      } else if (isYBottom) {
        vertical = DexAnchorPosition.BOTTOM
      }

      if (isXLeft) {
        horizontal = DexAnchorPosition.LEFT
      } else if (isXRight) {
        horizontal = DexAnchorPosition.RIGHT
      }
    } else {
      vertical =
        coords.y < centerY ? DexAnchorPosition.TOP : DexAnchorPosition.BOTTOM
      horizontal =
        coords.x < centerX ? DexAnchorPosition.LEFT : DexAnchorPosition.RIGHT
    }

    let position = horizontal ?? vertical

    if (vertical === DexAnchorPosition.TOP) {
      if (horizontal === DexAnchorPosition.LEFT) {
        position = DexAnchorPosition.TOP_LEFT
      } else if (horizontal === DexAnchorPosition.RIGHT) {
        position = DexAnchorPosition.TOP_RIGHT
      }
    } else if (vertical === DexAnchorPosition.BOTTOM) {
      if (horizontal === DexAnchorPosition.LEFT) {
        position = DexAnchorPosition.BOTTOM_LEFT
      } else if (horizontal === DexAnchorPosition.RIGHT) {
        position = DexAnchorPosition.BOTTOM_RIGHT
      }
    }

    const zone = {
      position: position!,
      vertical,
      horizontal,
      centerProximity,
    }

    return zone
  }

  /**
   * Set overlay
   *
   * @param param0
   * @returns
   */
  function setOverlay({
    overlay,
    clear,
  }: {
    overlay: DexOverlayInstance
    clear?: 'all' | 'sameType' | 'otherTypes' | DexOverlayType[]
  }) {
    if (clear === 'all') {
      setOpenedOverlays([overlay])
      return
    }

    if (clear === 'sameType') {
      setOpenedOverlays(existingOverlays => [
        ...existingOverlays.filter(existing => existing.type !== overlay.type),
        overlay,
      ])
      return
    }

    if (clear instanceof Array) {
      setOpenedOverlays(existingOverlays => [
        ...existingOverlays.filter(existing => !clear.includes(existing.type)),
        overlay,
      ])
      return
    }

    setOpenedOverlays(existingOverlays => {
      const overlayExists = existingOverlays.some(
        existing => existing.id === overlay.id,
      )

      if (overlayExists) {
        return existingOverlays
          .filter(existing =>
            clear === 'otherTypes' && existing.type !== overlay.type
              ? false
              : true,
          )
          .map(existing => (existing.id === overlay.id ? overlay : existing))
      }

      return [...existingOverlays, overlay]
    })
  }

  /**
   * Update overlay
   *
   * @param param0
   */
  function updateOverlay({
    id,
    overlay,
    clear,
  }: {
    id: string
    overlay: Partial<DexOverlayInstance>
    clear?: 'all' | 'sameType' | 'otherTypes'
  }) {
    const currentOverlay = getOverlayById(id)
    setOverlay({
      overlay: {
        ...currentOverlay!,
        ...overlay,
      },
      clear,
    })
  }

  /**
   * Set overlay
   *
   * @param id
   */
  function removeOverlayById(id: string) {
    setOpenedOverlays(existingOverlays =>
      existingOverlays.filter(overlay => overlay.id !== id),
    )
  }

  /**
   * Remove overlays
   *
   * @param filter
   */
  function removeOverlays(filter: OverlayFilter) {
    setOpenedOverlays(existingOverlays => {
      return filterOverlays(existingOverlays, filter, true)
    })
  }

  return {
    getOverlay,
    getOverlayById,
    getOverlays,
    getOverlaysByTargetId,
    getViewportZone,
    setOverlay,
    updateOverlay,
    removeOverlayById,
    removeOverlays,
  }
}
